<template>
  <div>
    <div id="menu-overlay" :class="{ open: isOpen }">
      <div id="overlay-logo">
        <router-link to="/"></router-link>
      </div>
      <div id="menu-icon" @click="toggleMenu">
        <MenuIconClose></MenuIconClose>
      </div>
      <div id="menu-overlay__menu">
        <ul>
          <li @click="toggleMenu"><router-link :to="{ path: '/our-work' }" class="our-work-link">Our
              Work</router-link></li>
          <li @click="toggleMenu"><router-link to="/our-people" class="our-people-link">Our People</router-link></li>
          <li @click="toggleMenu"><router-link class="contact-link"
              :to="{ path: '/', hash: '#contact' }">Contact</router-link>
          </li>
        </ul>
      </div>
    </div>
    <header class="header">
      <div class="header__nav">
        <div id="logo">
          <router-link to="/">
            <IgnisLogo></IgnisLogo>
          </router-link>
        </div>
        <div class="menu">
          <router-link :to="{ path: '/our-work' }" class="our-work-link">WORK</router-link>
          <a href="mailto:jackie@ignis.co.uk" data-track-type="enquiry" data-track-location="menu" class="connect">CONNECT</a>
          <div class="menu-icon" @click="toggleMenu" :class="{ open: isOpen }">
            <MenuIcon></MenuIcon>
          </div>
        </div>
      </div>
    </header>
  </div>

</template>

<script>
import { ref, nextTick, onMounted, onUnmounted, watch } from "vue";
import { useRoute } from "vue-router";
import MenuIcon from "@/components/svg/MenuIcon";
import MenuIconClose from "@/components/svg/MenuIconClose";
import IgnisLogo from "@/components/svg/IgnisLogo";

export default {
  name: "AppMenu",
  components: {
    MenuIcon,
    MenuIconClose,
    IgnisLogo,
  },
  setup() {
    const isOpen = ref(false);
    const isClosing = ref(false);
    const route = useRoute();
    let scrollHandler = null;

    // Toggle Menu
    const toggleMenu = () => {
      if (isOpen.value) {
        isClosing.value = true;
        setTimeout(() => {
          isOpen.value = false;
          isClosing.value = false;
        }, 300);
      } else {
        isOpen.value = true;
      }
    };

    const setupHeaderTheme = () => {
      const header = document.querySelector('.header');
      if (!header) return;

      // Clean up previous scroll handler
      if (scrollHandler) {
        window.removeEventListener('scroll', scrollHandler);
        scrollHandler = null;
      }

      // Remove any previous light override
      header.classList.remove('header--light');

      // Only set up scroll-based light toggle on homepage
      const pageClass = route.meta.pageClass;
      if (pageClass !== 'page-home') return;

      const lightSections = document.querySelectorAll('.slide-6, .showreel-container');
      if (lightSections.length === 0) return;

      const headerHeight = 80;

      scrollHandler = () => {
        const overLight = Array.from(lightSections).some(section => {
          const rect = section.getBoundingClientRect();
          return rect.top < headerHeight && rect.bottom > 0;
        });

        if (overLight) {
          header.classList.add('header--light');
        } else {
          header.classList.remove('header--light');
        }
      };

      window.addEventListener('scroll', scrollHandler, { passive: true });
    };

    onMounted(setupHeaderTheme);

    watch(() => route.path, async () => {
      await nextTick();
      setupHeaderTheme();
    });

    onUnmounted(() => {
      if (scrollHandler) {
        window.removeEventListener('scroll', scrollHandler);
      }
    });

    return {
      isOpen,
      toggleMenu,
    };
  },
};
</script>

<style lang="scss">
#logo {
  z-index: 1501;
  transition: all 0.3s ease;
}

#logo .logo path {
  fill: #E20034 !important;
  transition: fill 0.3s ease;
}

.burger-menu path {
  fill: #E20034 !important;
  transition: fill 0.3s ease;
}

.menu {
  height: 100%;
  margin-top: 8px;
  display: flex;
  align-items: center;
  flex-direction: row;
  transition: all 0.3s all;

  a {
    margin-right: 30px;
    font-family: "Bison", sans-serif;
    font-size: 2.2em;
    line-height: 0.9;
    font-weight: 300;
    text-align: center;
    color: #E20034;
    cursor: pointer;
    transition: all 0.3s ease;
  }

  .menu-icon {
    cursor: pointer;
    padding: 0 10px;
    transition: all 0.3s ease;
  }


  @media screen and (max-width: 1024px) {
    a {
      font-size: 1.6em;
      margin-right: 10px;
    }

  }

  @media screen and (max-width: 768px) {
    a.connect {
      display: none;
    }
  }
}


#menu-overlay {
  background: #000;
  height: 100%;
  width: 100% !important;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 33000;
  display: flex;
  /* Keep flex for layout, but use transform instead of display: none */
  align-items: start;
  justify-content: center;
  overflow: hidden;
  transform: translateX(-100%);
  /* Initially hidden to the left */
  transition: transform 0.3s ease-in-out;
  /* Smooth transition */

  &.open {
    transform: translateX(0);
    /* Slide in from the left */
  }

  &.closing {
    transform: translateX(100%);
    /* Slide out to the right */
  }

  &__menu {
    display: flex;
    flex-direction: column;
    align-items: start;
    justify-content: center;
    max-width: 960px;
    width: 960px;
    height: 100vh;
    overflow: hidden;
    transition: all 0.2s ease-in;

    ul {
      padding-left: 20px;
      overflow: hidden;

      li {
        margin: 4px 0;
      }
    }

    a {
      font-family: $font-stack-bison;
      font-size: 5em;
      line-height: 1;
      padding: 0 10px;
      display: flex;
      align-items: center;
      flex-direction: row;
      height: 110px;
      background-position: 0 50%;
      color: #ffffff;
      transition: 0.1s;

      &:hover {
        font-family: $font-stack-better-times;
        color: #03ffff;
        font-size: 5.6em;
      }

      @media screen and (max-width: 1024px) {
        font-size: 3em;
        height: 70px;
      }
    }
  }

  #overlay-logo {
    position: absolute;
    left: 20.5px;
    top: 20.5px;

    .logo {
      path {
        fill: white !important;
      }
    }
  }

  #menu-icon {
    position: absolute;
    right: 67px;
    top: 36px;
    cursor: pointer;
    width: 40px;

    @media screen and (max-width: 1024px) {
      right: 24px;
      top: 9px;
    }
  }
}

.menu-overlay-bg {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  opacity: 0;
  transition: opacity 0.3s ease-in-out;
  pointer-events: none;
}

.menu-overlay-bg.open {
  opacity: 1;
  pointer-events: all;
}

</style>